/**
 * Centralized error handling utilities
 */

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface AppError {
  message: string;
  severity: ErrorSeverity;
  code?: string;
  details?: any;
  timestamp: Date;
  retry?: () => Promise<any>;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private listeners: ((error: AppError) => void)[] = [];
  
  private constructor() {}
  
  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }
  
  public subscribe(listener: (error: AppError) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  public handle(error: any, severity: ErrorSeverity = 'error', retry?: () => Promise<any>): void {
    const appError: AppError = {
      message: this.extractMessage(error),
      severity,
      code: error?.code,
      details: error,
      timestamp: new Date(),
      retry
    };
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorHandler]', appError);
    }
    
    // Notify all listeners
    this.listeners.forEach(listener => listener(appError));
  }
  
  private extractMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return this.extractMessage(error.error);
    return 'An unexpected error occurred';
  }
  
  public async withRetry<T>(
    fn: () => Promise<T>,
    retries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: any;
    
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
        }
      }
    }
    
    throw lastError;
  }
}

export const errorHandler = ErrorHandler.getInstance();

/**
 * Validation utilities
 */
export const validators = {
  required: (value: any, fieldName: string = 'Field'): string | null => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },
  
  minLength: (value: string, min: number, fieldName: string = 'Field'): string | null => {
    if (!value || value.trim().length < min) {
      return `${fieldName} must be at least ${min} characters`;
    }
    return null;
  },
  
  maxLength: (value: string, max: number, fieldName: string = 'Field'): string | null => {
    if (value && value.length > max) {
      return `${fieldName} must be less than ${max} characters`;
    }
    return null;
  },
  
  url: (value: string): string | null => {
    if (!value) return null;
    try {
      new URL(value);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  },
  
  validFileName: (value: string): string | null => {
    if (!value) return 'File name is required';
    const invalid = /[<>:"/\\|?*]/g;
    if (invalid.test(value)) {
      return 'File name contains invalid characters';
    }
    return null;
  },
  
  uniqueIn: (value: string, list: string[], fieldName: string = 'Value'): string | null => {
    if (list.some(item => item.toLowerCase() === value.toLowerCase())) {
      return `${fieldName} already exists`;
    }
    return null;
  }
};

/**
 * Network status monitoring
 */
export class NetworkMonitor {
  private static instance: NetworkMonitor;
  private online: boolean = navigator.onLine;
  private listeners: ((online: boolean) => void)[] = [];
  
  private constructor() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }
  
  public static getInstance(): NetworkMonitor {
    if (!NetworkMonitor.instance) {
      NetworkMonitor.instance = new NetworkMonitor();
    }
    return NetworkMonitor.instance;
  }
  
  private handleOnline = () => {
    this.online = true;
    this.listeners.forEach(listener => listener(true));
  };
  
  private handleOffline = () => {
    this.online = false;
    this.listeners.forEach(listener => listener(false));
  };
  
  public isOnline(): boolean {
    return this.online;
  }
  
  public subscribe(listener: (online: boolean) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  public destroy(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
}

export const networkMonitor = NetworkMonitor.getInstance();

/**
 * Form validation helper
 */
export interface ValidationRule {
  validator: (value: any, formData?: any) => string | null;
  message?: string;
}

export interface ValidationSchema {
  [field: string]: ValidationRule[];
}

export function validateForm(
  formData: any,
  schema: ValidationSchema
): { [field: string]: string } {
  const errors: { [field: string]: string } = {};
  
  Object.keys(schema).forEach(field => {
    const rules = schema[field];
    const value = formData[field];
    
    for (const rule of rules) {
      const error = rule.validator(value, formData);
      if (error) {
        errors[field] = rule.message || error;
        break;
      }
    }
  });
  
  return errors;
}

/**
 * Async operation wrapper with error handling
 */
export async function safeAsync<T>(
  operation: () => Promise<T>,
  options: {
    errorMessage?: string;
    retry?: boolean;
    retries?: number;
    onError?: (error: any) => void;
  } = {}
): Promise<T | null> {
  try {
    if (options.retry) {
      return await errorHandler.withRetry(operation, options.retries);
    }
    return await operation();
  } catch (error) {
    errorHandler.handle(error, 'error');

    if (options.onError) {
      options.onError(error);
    }

    return null;
  }
}